import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, Chip, Field, PrimaryButton, SecondaryButton, formatDuration, inputClass, ringStyle, blockNonInteger } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import TrackStatusChip from "./TrackStatusChip";
import AudioUploader from "./AudioUploader";
import ArtworkUploader from "./ArtworkUploader";
import TerritoryPicker from "./TerritoryPicker";
import ContributorReleases, { contributorBody, contributorProblems, contributorsFromTrack, modeFromTrack } from "./ContributorReleases";
import { MESSAGES, fieldErrors, formatBytes, loadGenres, processingFailureText, trackApi, trackErrorMessage } from "./trackApi";

export const STEPS = ["Track File", "Metadata & Rights", "AI Disclosure", "Review & Publish"];

export const AI_OPTIONS = [
  { value: "human_made", title: "Fully human-made", text: "No AI tools were used to create this recording." },
  { value: "ai_assisted", title: "Contains AI-generated elements", text: "Some parts (e.g. instrumentation, mastering) used AI tools." },
  { value: "ai_generated", title: "Fully AI-generated / synthetic voice", text: "This recording is AI-generated or uses a synthesized voice." },
];

const EMPTY = {
  title: "", release_name: "", genre_id: "", language: "", description: "", release_date: "",
  composer: "", lyricist: "", isrc: "", iswc: "", explicit: "no", ownership: false, clip_start: "", clip_end: "",
  audio_quality: "standard", ai: "", ai_confirmed: false, territories: [],
  contributor_mode: "", contributors: [],
};

export function valuesFromTrack(t) {
  return {
    title: t.title || "",
    release_name: t.release_name || "",
    genre_id: t.genre ? String(t.genre.id) : "",
    language: t.language || "",
    description: t.description || "",
    release_date: (t.release_date || "").slice(0, 10),
    composer: t.composer || "",
    lyricist: t.lyricist || "",
    isrc: t.isrc || "",
    iswc: t.iswc || "",
    explicit: t.explicit_content ? "yes" : "no",
    ownership: !!t.ownership_confirmed,
    clip_start: t.clip?.start_seconds ?? "",
    clip_end: t.clip?.end_seconds ?? "",
    audio_quality: t.audio_quality || "standard",
    ai: t.ai_disclosure || "",
    ai_confirmed: !!t.ai_disclosure_confirmed,
    territories: Array.isArray(t.territories) ? t.territories : [],
    contributor_mode: modeFromTrack(t),
    contributors: contributorsFromTrack(t),
  };
}

// `partial` (Save as Draft, or starting the draft with the first file) leaves out anything still blank,
// because the API only insists on every field when the track is submitted.
export function buildBody(d, kind, partial = false) {
  const body = {
    release_name: (d.release_name || "").trim(),
    description: (d.description || "").trim(),
    composer: (d.composer || "").trim(),
    lyricist: (d.lyricist || "").trim(),
    isrc: (d.isrc || "").trim(),
    iswc: (d.iswc || "").trim(),
    explicit_content: d.explicit === "yes",
    ownership_confirmed: !!d.ownership,
    ai_disclosure_confirmed: !!d.ai_confirmed,
    audio_quality: d.audio_quality === "high_fidelity" ? "high_fidelity" : "standard",
    territories: Array.isArray(d.territories) ? d.territories : [],
    ...contributorBody(d.contributor_mode, d.contributors),
  };
  const title = (d.title || "").trim();
  if (title || !partial) body.title = title;
  if (d.genre_id) body.genre_id = Number(d.genre_id);
  if (d.language) body.language = d.language;
  if (d.release_date) body.release_date = d.release_date;
  if (d.ai) body.ai_disclosure = d.ai;
  if (kind === "clip" && d.clip_start !== "" && d.clip_end !== "") {
    body.clip = { start_seconds: Number(d.clip_start), end_seconds: Number(d.clip_end) };
  }
  return body;
}

// Server field name -> form field name
const FORM_FIELD = {
  title: "title", release_name: "release_name", genre_id: "genre_id", genre: "genre_id", language: "language",
  description: "description", release_date: "release_date", composer: "composer", lyricist: "lyricist", isrc: "isrc", iswc: "iswc",
  clip: "clip_end",
};
const STEP_FIELDS = ["title", "release_name", "genre_id", "language", "description", "release_date", "composer", "lyricist", "isrc", "iswc", "clip_start", "clip_end"];

const aiLabel = (value) => (AI_OPTIONS.find((o) => o.value === value) || {}).title || "—";

function Tabs({ step, setStep, maxStep }) {
  return (
    <nav className="flex flex-wrap gap-x-6 gap-y-2 border-b border-gray-100 pb-3" aria-label="Upload steps">
      {STEPS.map((label, i) => {
        const active = i === step;
        return (
          <button
            key={label}
            type="button"
            disabled={i > maxStep}
            onClick={() => setStep(i)}
            aria-current={active ? "step" : undefined}
            className={`text-[15px] font-semibold transition ${active ? "" : "text-gray-400"} ${i > maxStep ? "cursor-not-allowed" : "hover:text-gray-900"}`}
            style={active ? { color: ACCENT } : undefined}
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

function Notice({ tone = "red", children, ...rest }) {
  const tones = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    green: "bg-green-50 border-green-200 text-green-900",
  };
  return <div className={`rounded-xl border px-4 py-3 text-sm ${tones[tone]}`} {...rest}>{children}</div>;
}

function TrackForm({ routeId }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wantsResubmit = searchParams.get("resubmit") === "1";
  const isNew = !routeId || routeId === "new";
  const clientRef = useRef(
    (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  const savingRef = useRef(false);
  const submittingRef = useRef(false);
  const historyKey = useRef("");
  const trackRef = useRef(null);
  const kindRef = useRef("track");

  const [boot, setBoot] = useState({ loading: true, error: "" });
  const [rules, setRules] = useState(null);
  const [genres, setGenres] = useState([]);
  const [track, setTrack] = useState(null);
  const [history, setHistory] = useState([]);
  const [kind, setKindState] = useState("track");
  const [contentType, setContentType] = useState("audio");
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null); // set after a successful submit; drives the confirmation dialog
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState([]);

  const {
    register, reset, watch, trigger, getValues, setError, setValue,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY });

  trackRef.current = track;
  const setKind = (k) => {
    kindRef.current = k;
    setKindState(k);
  };

  // ---- load ----------------------------------------------------------------------------------------------
  useEffect(() => {
    // Right after the first file / "Save as Draft" the address changes to the new track's id; its data is already here.
    if (!isNew && trackRef.current && String(trackRef.current.id) === String(routeId)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [rulesResult, genreRows, existing] = await Promise.all([
          trackApi.rules(),
          loadGenres(),
          isNew ? Promise.resolve(null) : trackApi.detail(routeId),
        ]);
        if (cancelled) return;
        setRules(rulesResult);
        setGenres(genreRows);
        if (existing) {
          setTrack(existing);
          setKind(existing.kind);
          setContentType(existing.content_type === "video" ? "video" : "audio");
          setHistory(existing.history || []);
          historyKey.current = `${existing.id}:${existing.display_status}:${existing.version}`;
          reset(valuesFromTrack(existing));
          // Existing tracks open on the review step; any step can be revisited. "Resubmit Track" on a rejected track opens
          // Metadata & Rights (step 2) with the saved data, so the rights information and the ownership declaration can be corrected.
          setMaxStep(3);
          setStep(wantsResubmit && existing.can_resubmit ? 1 : 3);
        }
        setBoot({ loading: false, error: "" });
      } catch (e) {
        if (!cancelled) setBoot({ loading: false, error: trackErrorMessage(e, MESSAGES[500]) });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Keep the activity list current after saves, uploads and status changes.
  useEffect(() => {
    if (!track) return;
    const key = `${track.id}:${track.display_status}:${track.version}`;
    if (key === historyKey.current) return;
    historyKey.current = key;
    trackApi.detail(track.id).then((d) => d && setHistory(d.history || [])).catch(() => {});
  }, [track]);

  // Processing may finish while the artist is on another step or after a reload.
  const processing = track?.upload_status === "processing";
  useEffect(() => {
    if (!processing || step === 0) return undefined;
    const timer = setInterval(() => trackApi.detail(track.id).then(setTrack).catch(() => {}), 3000);
    return () => clearInterval(timer);
  }, [processing, step, track?.id]);

  const onTrackChange = useCallback((view) => setTrack(view), []);

  // ---- errors from the server ------------------------------------------------------------------------------
  const showServerErrors = (error) => {
    const errs = fieldErrors(error);
    if (!errs) return false;
    const leftovers = [];
    Object.entries(errs).forEach(([field, message]) => {
      const name = FORM_FIELD[field];
      if (name) setError(name, { type: "server", message });
      else leftovers.push(message);
    });
    if (leftovers.length) setProblems(leftovers);
    return true;
  };

  // ---- save ------------------------------------------------------------------------------------------------
  const persist = async (data, { partial = false } = {}) => {
    if (savingRef.current) return null;
    savingRef.current = true;
    setSaving(true);
    setProblems([]);
    try {
      const current = trackRef.current;
      const body = buildBody(data, kindRef.current, partial);
      const view = current
        ? await trackApi.update({ track_id: current.id, ...body })
        : await trackApi.create({ kind: kindRef.current, client_ref: clientRef.current, ...body });
      trackRef.current = view;
      setTrack(view);
      reset(valuesFromTrack(view));
      if (!current) navigate(`/artist-studio/tracks/${view.id}`, { replace: true });
      return view;
    } catch (e) {
      if (!showServerErrors(e)) toast.error(trackErrorMessage(e, MESSAGES[500]));
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Used by the file pickers: the first file starts the draft.
  const ensureTrack = async () => trackRef.current || persist(getValues(), { partial: true });

  const onSaveDraft = async () => {
    const view = await persist(getValues(), { partial: true });
    if (view) toast.success("Draft saved.");
  };

  // ---- steps ---------------------------------------------------------------------------------------------------
  const goTo = (n) => {
    setProblems([]);
    setStep(n);
    setMaxStep((m) => Math.max(m, n));
  };

  const filesReady = (t) => !!t && !!t.audio && t.upload_status === "ready_for_review" && !!t.artwork;

  const next = async () => {
    setProblems([]);
    if (step === 0) {
      const t = trackRef.current;
      const list = [];
      if (!t?.audio) list.push(contentType === "video" ? "Please upload your video file." : "Please upload your audio file.");
      else if (t.upload_status === "processing_failed") list.push(processingFailureText(t.upload_error));
      else if (t.upload_status !== "ready_for_review") list.push("Your audio is still being uploaded or processed. Please wait until it is ready.");
      if (!t?.artwork) list.push("Please add cover artwork.");
      if (list.length) return setProblems(list);
      return goTo(1);
    }
    if (step === 1) {
      const ok = await trigger(STEP_FIELDS);
      const d = getValues();
      const list = [];
      if (!d.ownership) list.push("Please confirm that you own or control the rights to this recording.");
      list.push(...contributorProblems(d.contributor_mode, d.contributors));
      if (rules.rights_info_required) {
        if (!d.composer.trim()) list.push("Please enter the composer. It is required for artists with rights-holder registrations.");
        if (!d.lyricist.trim()) list.push("Please enter the lyricist. It is required for artists with rights-holder registrations.");
      }
      if (!ok || list.length) return setProblems(list);
      return (await persist(d)) ? goTo(2) : undefined;
    }
    if (step === 2) {
      const d = getValues();
      if (!d.ai) return setProblems(["Please tell us how this track was made."]);
      if (!d.ai_confirmed) return setProblems(["Please confirm that your disclosure is accurate."]);
      return (await persist(d)) ? goTo(3) : undefined;
    }
    return undefined;
  };

  const back = () => {
    setProblems([]);
    setStep((s) => Math.max(0, s - 1));
  };

  // ---- submit for review ---------------------------------------------------------------------------------
  const onSubmitForReview = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setProblems([]);
    try {
      const saved = await persist(getValues());
      if (!saved) return;
      const wasRejected = trackRef.current?.status === "rejected";
      const result = await trackApi.submit(saved.id);
      setDone({
        resubmitted: wasRejected,
        id: saved.id,
        title: saved.title || getValues("title") || "Your track",
        live: (result?.display_status || result?.status) === "published",
      });
    } catch (e) {
      const errs = e?.response?.data?.details?.errors;
      if (errs) setProblems(Array.from(new Set(Object.values(errs))));
      else toast.error(trackErrorMessage(e, MESSAGES[500]));
      if (e?.response?.data?.code === "TRACK_ALREADY_SUBMITTED" && trackRef.current) {
        trackApi.detail(trackRef.current.id).then(setTrack).catch(() => {});
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // ---- render ------------------------------------------------------------------------------------------------
  if (boot.loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loading />
      </div>
    );
  }
  if (boot.error || !rules) {
    return (
      <Notice role="alert">
        {boot.error || MESSAGES[500]}{" "}
        <Link to="/artist-studio/tracks" className="underline font-semibold">Back to my catalogue</Link>
      </Notice>
    );
  }

  const locked = !!track && !track.can_edit;
  const languages = Array.from(new Set([...(rules.languages || []), ...(track?.language ? [track.language] : [])]));
  const genreOptions = track?.genre && !genres.some((g) => g.id === track.genre.id) ? [...genres, track.genre] : genres;
  const audioQuality = watch("audio_quality");
  const clipStart = watch("clip_start");
  const clipEnd = watch("clip_end");
  const clipLength = Number(clipEnd) - Number(clipStart);
  const maxClip = rules.audio.max_clip_seconds;
  const values = watch();
  const hide = (n) => (step === n ? "" : "hidden");

  const text = (name, label, opts = {}) => (
    <Field label={label} required={opts.required} hint={opts.hint} error={errors[name]?.message}>
      <input
        type={opts.type || "text"}
        className={inputClass(errors[name])}
        style={ringStyle}
        aria-label={label}
        placeholder={opts.placeholder}
        maxLength={opts.maxLength}
        {...register(name, opts.rules)}
      />
    </Field>
  );

  const failed = track && ["processing_failed", "upload_failed"].includes(track.upload_status);
  const scanState = !track ? null : processing || track.upload_status === "uploaded" ? "scanning" : failed ? "flagged" : filesReady(track) ? "clear" : null;
  const reviewRows = [
    ["Title", values.title || "—"],
    ["Genre", genreOptions.find((g) => String(g.id) === String(values.genre_id))?.name || "—"],
    ["Language", values.language || "—"],
    ["Release date", values.release_date || "—"],
    [contentType === "video" ? "Video" : "Audio", track?.audio ? `${track.audio.file_name} (${formatBytes(track.audio.file_size)}${track.duration ? `, ${formatDuration(track.duration)}` : ""})` : "—"],
    ...(contentType === "audio" ? [["Audio quality", audioQuality === "high_fidelity" ? "High-Fidelity (lossless)" : "Standard (MP3/AAC)"]] : []),
    ["Cover art", track?.artwork ? track.artwork.file_name : "—"],
    ["Ownership", values.ownership ? "Declared sole owner" : "Not confirmed"],
    ["Contributor releases", values.contributor_mode === "solo" ? "Solo recording" : values.contributor_mode === "others" ? `${(values.contributors || []).filter((c) => (c.name || "").trim()).length} contributor(s), releases held` : "Not answered"],
    ["AI disclosure", aiLabel(values.ai)],
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/artist-studio/tracks" className="text-sm font-semibold underline">← Back to my catalogue</Link>
        <div className="flex items-center gap-3">
          {track && <TrackStatusChip status={track.display_status} />}
          {!locked && (
            <SecondaryButton onClick={onSaveDraft} disabled={saving || submitting} className="!py-2 border-green-800 text-green-900">
              {saving ? "Saving…" : "Save as Draft"}
            </SecondaryButton>
          )}
        </div>
      </div>

      {track?.status === "rejected" && (
        <Notice role="alert">
          <p className="font-semibold">This track was rejected</p>
          <p className="mt-1" data-testid="rejection-reason">{track.rejection_reason || "No reason was recorded."}</p>
          {track.can_resubmit ? (
            <p className="mt-1">
              Correct the rights information and contributor details, then resubmit. You can resubmit a rejected track once, so please check
              everything before you send it.
            </p>
          ) : (
            <p className="mt-1">
              This track can no longer be changed.{" "}
              <Link to={`/artist-studio/tracks/${track.id}/view`} className="underline font-semibold">Open the track page</Link>
            </p>
          )}
        </Notice>
      )}
      {track?.display_status === "pending_review" && (
        <Notice tone="amber">This track has been submitted and is under review. It can't be edited until the review is finished.</Notice>
      )}
      {track?.display_status === "approved" && <Notice tone="amber">This track has been approved. It will appear on your public page once it is published.</Notice>}
      {track?.display_status === "published" && <Notice tone="green">This track is live on your public page.</Notice>}

      <Tabs step={step} setStep={(n) => { setProblems([]); setStep(n); }} maxStep={maxStep} />

      {/* Step 1: Track file */}
      <section className={`space-y-6 ${hide(0)}`} aria-label="Track File">
        <div>
          <p className="text-sm font-bold text-gray-900 mb-1.5">Content type</p>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Content type">
            <Chip selected={contentType === "audio"} disabled={!!track?.audio} onClick={() => setContentType("audio")}>Audio track</Chip>
            <Chip selected={contentType === "video"} disabled={!!track?.audio} onClick={() => { setContentType("video"); setValue("audio_quality", "standard"); }}>Video</Chip>
          </div>
        </div>
        <AudioUploader track={track} kind={kind} contentType={contentType} rules={rules} disabled={locked} onTrackChange={onTrackChange} ensureTrack={ensureTrack} />
        {contentType === "audio" && (
          <div>
            <p className="text-sm font-bold text-gray-900 mb-1.5">Audio quality</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Audio quality">
              <Chip selected={audioQuality !== "high_fidelity"} disabled={locked} onClick={() => setValue("audio_quality", "standard", { shouldDirty: true })}>Standard (MP3/AAC)</Chip>
              <Chip selected={audioQuality === "high_fidelity"} disabled={locked} onClick={() => setValue("audio_quality", "high_fidelity", { shouldDirty: true })}>High-Fidelity (WAV/FLAC, lossless)</Chip>
            </div>
            <p className="text-sm text-gray-500 mt-2" data-testid="audio-quality-help">
              {audioQuality === "high_fidelity"
                ? "Streamed at full lossless quality to Premium subscribers; transcoded to standard for Basic."
                : "Streamed as a compressed file to all listeners."}
            </p>
          </div>
        )}
        <div>
          <p className="text-sm font-bold text-gray-900 mb-2">Cover art</p>
          <ArtworkUploader track={track} rules={rules} disabled={locked} onTrackChange={onTrackChange} ensureTrack={ensureTrack} />
        </div>
      </section>

      {/* Step 2: Metadata & rights */}
      <section className={`${hide(1)}`} aria-label="Metadata and rights">
        <fieldset disabled={locked} className="space-y-4 min-w-0">
          {text("title", kind === "clip" ? "Clip title" : "Title", {
            required: true,
            placeholder: kind === "clip" ? "Clip title" : "Track title",
            maxLength: 200,
            rules: { validate: (v) => (v.trim().length > 0 ? !/[<>]/.test(v) || "Track title cannot contain < or >." : MESSAGES.title) },
          })}
          <Field label="Artist" hint="Your verified artist name.">
            <input className={inputClass(false)} value={rules.artist_name || ""} readOnly aria-label="Artist" />
          </Field>
          {text("release_name", "Album / release", { hint: "Optional", maxLength: 200 })}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Genre" required error={errors.genre_id?.message}>
              <select className={inputClass(errors.genre_id)} style={ringStyle} aria-label="Genre" {...register("genre_id", { required: "Please choose a genre." })}>
                <option value="">{genreOptions.length ? "e.g. Afrobeat" : "No genres available yet"}</option>
                {genreOptions.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </Field>
            <Field label="Language" required error={errors.language?.message}>
              <select className={inputClass(errors.language)} style={ringStyle} aria-label="Language" {...register("language", { required: "Please choose a language." })}>
                <option value="">Select a language</option>
                {languages.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
          </div>
          <TerritoryPicker
            value={values.territories}
            disabled={locked}
            onChange={(list) => setValue("territories", list, { shouldDirty: true })}
          />
          {text("release_date", "Release date", { type: "date", required: true, rules: { required: "Please enter a release date." } })}
          <Field label="Description" hint="Optional" error={errors.description?.message}>
            <textarea rows={3} maxLength={2000} className={inputClass(errors.description)} style={ringStyle} aria-label="Description" {...register("description")} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            {text("composer", "Composer", {
              required: rules.rights_info_required,
              hint: rules.rights_info_required ? "Required because you have rights-holder registrations." : "Optional",
              maxLength: 200,
            })}
            {text("lyricist", "Lyricist", {
              required: rules.rights_info_required,
              hint: rules.rights_info_required ? "Required because you have rights-holder registrations." : "Optional",
              maxLength: 200,
            })}
          </div>
          {text("isrc", "ISRC", {
            hint: "Optional. For example IN-ABC-24-00001.",
            rules: { pattern: { value: /^$|^[A-Za-z]{2}-?[A-Za-z0-9]{3}-?[0-9]{2}-?[0-9]{5}$/, message: "ISRC must look like IN-ABC-24-00001." } },
          })}
          {text("iswc", "ISWC", {
            hint: "Optional. The work's identifier in the rights societies' registers, for example T-123.456.789-0.",
            rules: { pattern: { value: /^$|^T-?[0-9]{3}\.?[0-9]{3}\.?[0-9]{3}-?[0-9]$/i, message: "ISWC must look like T-123.456.789-0." } },
          })}
          <Field label="Explicit content" required>
            <div className="flex gap-6" role="radiogroup" aria-label="Explicit content">
              <label className="flex items-center gap-2"><input type="radio" value="no" {...register("explicit")} /> No</label>
              <label className="flex items-center gap-2"><input type="radio" value="yes" {...register("explicit")} /> Yes</label>
            </div>
          </Field>

          {kind === "clip" && (
            <div>
              <p className="text-sm font-bold text-gray-900 mb-1.5">Clip position <span className="text-red-500">*</span></p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Start (seconds)" error={errors.clip_start?.message}>
                  <input
                    type="number" inputMode="numeric" min="0" step="1" onKeyDown={blockNonInteger} className={inputClass(errors.clip_start)} style={ringStyle} aria-label="Clip start"
                    {...register("clip_start", { required: "Enter where the clip starts.", min: { value: 0, message: "Clip start cannot be negative." } })}
                  />
                </Field>
                <Field label="End (seconds)" error={errors.clip_end?.message}>
                  <input
                    type="number" inputMode="numeric" min="0" step="1" onKeyDown={blockNonInteger} className={inputClass(errors.clip_end)} style={ringStyle} aria-label="Clip end"
                    {...register("clip_end", {
                      required: "Enter where the clip ends.",
                      validate: (v, all) => {
                        const length = Number(v) - Number(all.clip_start);
                        if (!(length > 0)) return "The clip must end after it starts.";
                        if (length > maxClip) return `A clip can be at most ${maxClip} seconds long.`;
                        return true;
                      },
                    })}
                  />
                </Field>
              </div>
              {clipLength > 0 && (
                <p className="text-sm text-gray-600 mt-2" data-testid="clip-length">
                  Clip length: {formatDuration(clipLength) || `${clipLength}s`} ({clipLength} seconds)
                </p>
              )}
            </div>
          )}

          <ContributorReleases
            mode={values.contributor_mode}
            onModeChange={(m) => setValue("contributor_mode", m)}
            contributors={values.contributors || []}
            onChange={(list) => setValue("contributors", list)}
            disabled={locked}
          />

          <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-[#faf9f7] px-4 py-4">
            <input type="checkbox" className="mt-1" aria-label="Ownership confirmation" {...register("ownership")} />
            <span className="text-gray-800">
              I solely own or control all rights to this recording, or have a release from every contributor, per the{" "}
              <span className="font-semibold" style={{ color: ACCENT }}>Artist Terms &amp; Sole-Ownership Warranty</span> and{" "}
              <span className="font-semibold" style={{ color: ACCENT }}>Contributor Release Waiver</span>.
              <span className="text-red-500"> *</span>
            </span>
          </label>
        </fieldset>
      </section>

      {/* Step 3: AI disclosure */}
      <section className={`space-y-3 ${hide(2)}`} aria-label="AI disclosure">
        <p className="text-gray-600">Tell us how this track was made. Misrepresenting AI involvement can result in takedown.</p>
        <fieldset disabled={locked} className="space-y-3 min-w-0">
          {AI_OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`block rounded-xl border px-5 py-4 cursor-pointer ${values.ai === o.value ? "border-2 border-gray-900" : "border-gray-200"}`}
            >
              <input type="radio" value={o.value} className="sr-only" aria-label={o.title} {...register("ai")} />
              <span className="block font-bold text-gray-900">{o.title}</span>
              <span className="block text-sm text-gray-500">{o.text}</span>
            </label>
          ))}
          <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-[#faf9f7] px-4 py-4">
            <input type="checkbox" className="mt-1" aria-label="AI disclosure confirmation" {...register("ai_confirmed")} />
            <span className="text-gray-800">
              I warrant this disclosure is accurate and I have rights to any AI tools used, per the{" "}
              <span className="font-semibold" style={{ color: ACCENT }}>Artist Terms &amp; Sole-Ownership Warranty</span>.
            </span>
          </label>
        </fieldset>
      </section>

      {/* Step 4: Review & publish */}
      <section className={`space-y-4 ${hide(3)}`} aria-label="Review and publish">
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-5 space-y-1.5" data-testid="review-summary">
          {reviewRows.map(([k, v]) => (
            <p key={k} className="text-gray-900"><span className="text-gray-500">{k} — </span>{v}</p>
          ))}
        </div>

        {scanState === "scanning" && (
          <div className="rounded-2xl border border-gray-200 px-5 py-4 flex items-center gap-3" role="status">
            <span className="inline-block w-5 h-5 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin" aria-hidden="true" />
            <span>Checking your {contentType === "video" ? "video" : "audio"}…</span>
          </div>
        )}
        {scanState === "clear" && <Notice tone="green" role="status">✓ No problems found — ready to submit</Notice>}
        {scanState === "flagged" && (
          <Notice role="alert">
            <p className="font-semibold">
              {track.upload_status === "processing_failed" ? "We couldn't process your audio" : "Your upload didn't finish"}
            </p>
            <p className="mt-1">{track.upload_status === "processing_failed" ? processingFailureText(track.upload_error) : MESSAGES.uploadFailed}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {track.upload_status === "processing_failed" && !locked && (
                <SecondaryButton className="!py-1.5 !px-4 text-sm" onClick={async () => {
                  try { setTrack(await trackApi.retry(track.id)); } catch (e) { toast.error(trackErrorMessage(e, MESSAGES.processingFailed)); trackApi.detail(track.id).then(setTrack).catch(() => {}); }
                }}>
                  Retry Processing
                </SecondaryButton>
              )}
              <SecondaryButton className="!py-1.5 !px-4 text-sm" onClick={() => setStep(0)}>Go to Track File</SecondaryButton>
            </div>
          </Notice>
        )}
        {!scanState && track && !locked && (
          <Notice tone="amber">Add your audio file and cover art on the Track File step before submitting.</Notice>
        )}
      </section>

      {problems.length > 0 && (
        <Notice role="alert">
          <p className="font-semibold">Please fix the following:</p>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </Notice>
      )}

      <div className="flex items-center justify-between pt-2">
        {step > 0 ? <SecondaryButton onClick={back}>Back</SecondaryButton> : <span />}
        {!locked && step < 3 && (
          <PrimaryButton onClick={next} disabled={saving} data-testid="next">{saving ? "Saving…" : "Next"}</PrimaryButton>
        )}
        {!locked && step === 3 && (
          <PrimaryButton
            onClick={onSubmitForReview}
            disabled={submitting || saving || !filesReady(track)}
            data-testid="submit-review"
          >
            {submitting ? "Submitting…" : track?.status === "rejected" ? "Resubmit for review" : "Submit for review"}
          </PrimaryButton>
        )}
      </div>

      {done && (
        <div
          className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="track-done-title"
          data-testid="submit-done"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-2xl" style={{ color: ACCENT }} aria-hidden="true">✓</div>
            <h2 id="track-done-title" className="text-xl font-bold text-gray-900">
              {done.live ? `“${done.title}” is live` : done.resubmitted ? `“${done.title}” was resubmitted` : `“${done.title}” was submitted`}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {done.live
                ? "Fans can now stream it."
                : done.resubmitted
                  ? "It is back in review and the audio fingerprint check will run again. This was your one resubmission."
                  : "It is now under review and will go live once our team has approved it."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
                            <SecondaryButton onClick={() => navigate("/artist-studio/tracks")} data-testid="view-catalogue">View in Catalogue</SecondaryButton>
              <PrimaryButton onClick={() => navigate(`/artist-studio/monetization?track=${done.id}`)} data-testid="set-pricing">Set Pricing</PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && step === 3 && (
        <div className="pt-4 border-t border-gray-100">
          <h3 className="font-bold text-gray-900 mb-2">Activity</h3>
          <ul className="space-y-1.5 text-sm text-gray-700" data-testid="track-history">
            {history.map((h) => (
              <li key={h.id} className="flex flex-wrap gap-x-3">
                <span className="text-gray-400 tabular-nums">{new Date(h.at).toLocaleString()}</span>
                <span className="font-medium">{String(h.event).replace(/_/g, " ")}</span>
                {h.message && <span className="text-gray-500 truncate">{h.message}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function TrackUpload() {
  const account = useStudioAccount();
  const { id } = useParams();
  return (
    <StudioGate account={account} active="tracks" heading="Upload a track" tabs={[]} maxWidth="max-w-3xl">
      <TrackForm routeId={id} />
    </StudioGate>
  );
}
